import { useEffect, useState } from 'react';
import { useLoyaltyClubStore } from '~contexts/mobx';
import type { LoyaltyClubTierReward } from '~stores/loyalty-club';

/**
 * The club's tier ladder, read once per page load: the skeleton the rewards
 * inventory table (slice 10) groups by tier, reusing the same
 * `fetchRoadToRewardsClub` read `useCampaignWindow` already makes for the
 * campaign dates. A second fetch rather than a shared one: the two hooks read
 * different parts of the same club for unrelated reasons, matching how
 * `useCampaignCouponStock` is already its own sibling read on this page.
 */
export type CampaignTierLadderState =
    | { status: 'loading' }
    | { status: 'ready'; tierRewards: LoyaltyClubTierReward[] }
    | { status: 'unavailable'; reason: string };

const CLUB_MISSING =
    'No Road to Rewards campaign club is configured in this environment.';

const CLUB_FAILED =
    'The Road to Rewards campaign could not be loaded, so its milestone ladder cannot be read.';

export const useCampaignTierLadder = (): CampaignTierLadderState => {
    const loyaltyClubStore = useLoyaltyClubStore();
    const [state, setState] = useState<CampaignTierLadderState>({
        status: 'loading',
    });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const club = await loyaltyClubStore.fetchRoadToRewardsClub();
                if (cancelled) return;
                if (!club) {
                    setState({ status: 'unavailable', reason: CLUB_MISSING });
                    return;
                }
                setState({
                    status: 'ready',
                    tierRewards: club.tierRewards ?? [],
                });
            } catch {
                if (!cancelled) {
                    setState({ status: 'unavailable', reason: CLUB_FAILED });
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [loyaltyClubStore]);

    return state;
};
